import styled, { css } from 'styled-components';
import { breakpoints } from '@klv/shared/utils/screen-size';
import Grid from '@klv/utils/grid';
import { generateGridStyles } from '@klv/utils/grid/styles';
import Icon from '@klv/atoms/icon';
import Typography from '@klv/atoms/typography';
import Buttons from '@klv/molecules/buttons';

import type { DefaultTheme } from 'styled-components';
import type { TColorVariant } from '@klv/shared/styles/types';
import type { ICarouselButton, IGalleryCards } from '../types';

const carouselButtonWidth = '6rem';

const nestedGridStyles = (removePadding = false) => css`
  grid-column: 1 / -1;
  grid-row-gap: 2.4rem;
  padding: ${removePadding ? '0' : ''};
  width: 100%;
`;

const setGridPadding = (cssProps: string[], theme: DefaultTheme) => {
  const paddingCssValues: string[] = [];

  breakpoints.forEach((breakpoint) => {
    const spacingValues = cssProps.map((cssProp) => (`${cssProp}: ${theme.screenSize[`s${breakpoint}`].gridHorizontalPadding}rem;`));
    const joinedSpacingValues = spacingValues.join('\n');

    if (breakpoint > 380) {
      paddingCssValues.push(`
        ${theme.screenSize[`s${breakpoint}`].mediaQuery} {
          ${joinedSpacingValues}
        }
      `);
    } else {
      paddingCssValues.push(joinedSpacingValues);
    }
  });

  return paddingCssValues.join('\n\n');
};

// Placed before other components to ensure --carousel-padding is declared before any code that uses it.
export const StyledGallery = styled.div<{ $backgroundColor: TColorVariant; }>`
  ${({ $backgroundColor, theme }) => css`
    ${setGridPadding(['--carousel-padding'], theme)}
    background-color: ${theme.gallery[$backgroundColor].backgroundColor};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    row-gap: 3.6rem;
    ${theme.globalPadding}
  `}
`;

export const ButtonIcon = styled(Icon)`
  margin: auto;
  transition: color 0.3s ease;
`;

export const Carousel = styled.div`
  ${({ theme }) => css`
    display: flex;
    grid-column: 1 / -1;
    margin: 0 auto;
    max-width: 100%;
    place-items: center;
    position: relative;

    ${theme.screenSize.s1366.mediaQuery} {
      max-width: ${theme.screenSize.s1366.size}rem;
    }
  `}
`;

export const CarouselButton = styled.button<ICarouselButton>`
  ${({ $buttonColor, $show, theme }) => css`
    background-color: ${theme.gallery?.[$buttonColor].buttonColor ?? theme.gallery.buttonColor};
    border: none;
    border-radius: 50%;
    box-shadow: 0 1.3rem 3.7rem rgba(0, 0, 0, 0.21);
    color: ${theme.gallery[$buttonColor]?.iconColor ?? theme.gallery.iconColor};
    cursor: pointer;
    display: none;
    height: ${carouselButtonWidth};
    position: absolute;
    transition: background-color 0.3s ease;
    visibility: ${!$show ? 'hidden' : ''};
    width: ${carouselButtonWidth};
    z-index: 4;

    ${theme.screenSize.s1024.mediaQuery} {
      display: block;
    }
  `}

  ${({ theme, $variant }) => ($variant === 'previous' ? css`
    left: calc(var(--carousel-padding) - (${carouselButtonWidth} / 2));

    ${theme.screenSize.s1366.mediaQuery} {
      left: calc(var(--carousel-padding) / 2);
    }

    ${ButtonIcon} {
      transform: rotate(90deg);
    }
  ` : css`
    right: calc(var(--carousel-padding) - (${carouselButtonWidth} / 2));

    ${theme.screenSize.s1200.mediaQuery} {
      right: calc(var(--carousel-padding) + (6rem / 3));
    }

    ${theme.screenSize.s1366.mediaQuery} {
      right: calc(var(--carousel-padding) * 2);
    }

    ${ButtonIcon} {
      transform: rotate(-90deg);
    }
  `)}

  &:hover {
    background-color: ${({ theme }) => theme.gallery.buttonHoverColor};
    color: ${({ $buttonColor, theme }) => theme.gallery[$buttonColor]?.iconHoverColor ?? ''};
  }
`;

export const GalleryCards = styled.div<IGalleryCards>`
  ${({ theme }) => css`
    max-width: 100%;
    // Targeting the scroll box.
    .gallery-scroll-box {
      column-gap: ${theme.screenSize.s380.gridColumnGap}rem; // Setting the column gap only for mobile, because the value stays the same for all screen sizes.
      cursor: grab;
      display: grid;
      grid-auto-columns: calc(100% - var(--carousel-padding) - (${theme.screenSize.s380.gridColumnGap}rem * 2));
      grid-auto-flow: column;
      grid-column: 1 / -1;
      min-width: 100%;
      overflow-x: auto !important; // Needs to be !important, so that it overrides the library's overflow property.
      padding-block: 1.2rem;
      scroll-behavior: smooth;
      ${setGridPadding(['padding-left', 'padding-right'], theme)}

      &::-webkit-scrollbar {
        display: none;
      }

      ${theme.screenSize.s576.mediaQuery} {
        grid-auto-columns: calc(50% - (var(--carousel-padding) - ${theme.screenSize.s380.gridColumnGap}rem));
      }

      ${theme.screenSize.s1024.mediaQuery} {
        grid-auto-columns: calc((100% / 3) - (var(--carousel-padding) - ${theme.screenSize.s380.gridColumnGap}rem));
      }

      @media (prefers-reduced-motion) {
        scroll-behavior: auto;
      }

      &::after {
        ${theme.screenSize.s1024.mediaQuery} {
          content: '';
        }
      }
    }
  `}
`;

export const Headline = styled(Typography)`
  ${({ theme }) => css`
    grid-column: 1 / -1;

    ${theme.screenSize.s576.mediaQuery} {
      grid-column: 1 / span 10;
    }

    ${theme.screenSize.s1024.mediaQuery} {
      grid-column: 1 / -1;
    }
  `}
`;

export const IntroContent = styled(Grid)`
  ${nestedGridStyles()}

  ${({ theme }) => theme.screenSize.s1200.mediaQuery} {
    place-items: start end;
  }
`;

export const IntroText = styled(Grid)`
  ${({ theme }) => css`
    ${nestedGridStyles(true)}
    ${generateGridStyles({ columns: { 1200: 9 }, omitPadding: true, theme })}

    ${theme.screenSize.s1200.mediaQuery} {
      grid-column: 1 / span 9;
    }
  `}
`;

export const StyledButtons = styled(Buttons)`
  ${({ theme }) => css`
    grid-column: 1 / -1;

    ${theme.screenSize.s1200.mediaQuery} {
      grid-column: -1 / -4;
    }
  `}
`;

export const Subhead = styled(Typography)`
  ${({ theme }) => css`
    grid-column: 1 / -1;

    ${theme.screenSize.s576.mediaQuery} {
      grid-column: 1 / span 11;
    }

    ${theme.screenSize.s768.mediaQuery} {
      grid-column: 1 / -1;
    }

    ${theme.screenSize.s1024.mediaQuery} {
      grid-column: 1 / span 10;
    }

    ${theme.screenSize.s1200.mediaQuery} {
      grid-column: 1 / span 9;
    }
  `}
`;
